import React, { useState, useRef, useMemo } from 'react';
import {
  Loader2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  ArrowLeft,
  Target,
  Zap,
  Clock,
  CheckCircle,
  Circle,
  Play,
  RefreshCw,
  ExternalLink,
  Bug,
  BookOpen,
  ListTodo,
  AlertTriangle,
  ClipboardList,
  Layers,
  Users,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { useSprintTeamMetrics } from '../../hooks/useJira';
import { useSprintGoals, calculateGoalProgress } from '../../hooks/useSprintGoals';
import TechnologyFilters from '../../components/TechnologyFilters';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  AreaChart,
  Area,
} from 'recharts';

const TEAM_NAMES = {
  platform: 'Platform',
  clientPortal: 'Client Portal',
  analytics: 'Analytics',
  serviceCloud: 'Service Cloud',
};

// Board IDs for Sprint Goals feature (Platform and Client Portal only)
const BOARD_IDS = {
  platform: 899,
  clientPortal: 898,
};

// Sortable header component
function SortableHeader({ label, sortKey, currentSort, onSort, align = 'left' }) {
  const isActive = currentSort.key === sortKey;
  const alignClass = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';

  return (
    <th
      className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none`}
    >
      <button
        onClick={() => onSort(sortKey)}
        className={`flex items-center gap-1 w-full ${alignClass}`}
      >
        <span>{label}</span>
        {isActive ? (
          currentSort.direction === 'asc' ? (
            <ArrowUp className="w-3 h-3 text-amber-600" />
          ) : (
            <ArrowDown className="w-3 h-3 text-amber-600" />
          )
        ) : (
          <ArrowUpDown className="w-3 h-3 text-gray-400" />
        )}
      </button>
    </th>
  );
}

// Generic sorting function
function sortData(data, sortKey, direction, customComparator) {
  if (!sortKey || !data) return data;

  return [...data].sort((a, b) => {
    let aVal = a[sortKey];
    let bVal = b[sortKey];

    // Handle custom comparator for complex sorting
    if (customComparator) {
      return customComparator(a, b, direction);
    }

    // Handle null/undefined values
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';

    // Handle string comparison
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      const comparison = aVal.localeCompare(bVal);
      return direction === 'asc' ? comparison : -comparison;
    }

    // Handle number comparison
    if (direction === 'asc') {
      return aVal - bVal;
    }
    return bVal - aVal;
  });
}

// Hook for managing sort state
function useTableSort(defaultKey = null, defaultDirection = 'asc') {
  const [sort, setSort] = useState({ key: defaultKey, direction: defaultDirection });

  const handleSort = (key) => {
    setSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  return [sort, handleSort];
}

function StatCard({ title, value, subtitle, icon: Icon, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-amber-100 text-amber-600',
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

function getStatusIcon(statusCategory) {
  switch (statusCategory) {
    case 'done':
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    case 'indeterminate':
      return <Play className="w-4 h-4 text-amber-500" />;
    default:
      return <Circle className="w-4 h-4 text-gray-400" />;
  }
}

function getStatusColor(statusCategory) {
  switch (statusCategory) {
    case 'done':
      return 'bg-green-100 text-green-800';
    case 'indeterminate':
      return 'bg-amber-100 text-amber-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

function IssueTypeCard({ title, icon: Icon, color, data }) {
  if (!data || data.total === 0) return null;

  const colorClasses = {
    red: { bg: 'bg-red-50', border: 'border-red-200', icon: 'bg-red-100 text-red-600', text: 'text-red-700' },
    blue: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'bg-amber-100 text-amber-600', text: 'text-amber-700' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'bg-purple-100 text-purple-600', text: 'text-purple-700' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'bg-orange-100 text-orange-600', text: 'text-orange-700' },
    indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', icon: 'bg-indigo-100 text-indigo-600', text: 'text-indigo-700' },
    gray: { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'bg-gray-100 text-gray-600', text: 'text-gray-700' },
  };

  const colors = colorClasses[color] || colorClasses.gray;
  const progress = data.total > 0 ? (data.completed / data.total) * 100 : 0;

  return (
    <div className={`${colors.bg} rounded-lg border ${colors.border} p-4`}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`p-2 rounded-lg ${colors.icon}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <h5 className={`text-sm font-semibold ${colors.text}`}>{title}</h5>
          <p className="text-xs text-gray-500">{data.total} issues • {data.totalPoints} pts</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-white rounded-full h-2 mb-3">
        <div
          className="bg-green-500 h-2 rounded-full transition-all"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Status breakdown */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-white rounded p-2">
          <p className="text-lg font-bold text-gray-500">{data.todo}</p>
          <p className="text-xs text-gray-400">To Do</p>
        </div>
        <div className="bg-white rounded p-2">
          <p className="text-lg font-bold text-amber-600">{data.inProgress}</p>
          <p className="text-xs text-gray-400">In Progress</p>
        </div>
        <div className="bg-white rounded p-2">
          <p className="text-lg font-bold text-green-600">{data.completed}</p>
          <p className="text-xs text-gray-400">Done</p>
        </div>
      </div>
    </div>
  );
}

function StatusCard({ title, icon: Icon, color, data }) {
  if (!data || data.total === 0) return null;

  const colorClasses = {
    gray: { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'bg-gray-100 text-gray-600', text: 'text-gray-700', accent: 'text-gray-600' },
    blue: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'bg-amber-100 text-amber-600', text: 'text-amber-700', accent: 'text-amber-600' },
    green: { bg: 'bg-green-50', border: 'border-green-200', icon: 'bg-green-100 text-green-600', text: 'text-green-700', accent: 'text-green-600' },
  };

  const colors = colorClasses[color] || colorClasses.gray;

  // Compact issue type summary - only show non-zero counts
  const typeBreakdown = [
    { label: 'S', count: data.stories, title: 'Stories' },
    { label: 'B', count: data.bugs, title: 'Bugs' },
    { label: 'T', count: data.tasks, title: 'Tasks' },
    { label: 'ST', count: data.subTasks, title: 'Sub-tasks' },
    { label: 'D', count: data.defects, title: 'Defects' },
  ].filter(item => item.count > 0);

  return (
    <div className={`${colors.bg} rounded-lg border ${colors.border} p-3`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${colors.icon}`}>
            <Icon className="w-3.5 h-3.5" />
          </div>
          <h5 className={`text-sm font-semibold ${colors.text}`}>{title}</h5>
        </div>
        <div className="text-right">
          <p className={`text-lg font-bold ${colors.accent}`}>{data.total}</p>
          <p className="text-xs text-gray-400">{data.totalPoints} pts</p>
        </div>
      </div>
      {/* Compact type breakdown */}
      {typeBreakdown.length > 0 && (
        <div className="flex gap-2 mt-2 text-xs">
          {typeBreakdown.map(item => (
            <span key={item.label} className="bg-white px-1.5 py-0.5 rounded text-gray-600" title={item.title}>
              {item.label}: {item.count}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Sprint Goals Progress Section (read-only display)
function SprintGoalsSection({ boardId, sprintId, sprintIssues, onNavigate }) {
  const { data: goalsData, isLoading } = useSprintGoals(boardId, sprintId);
  const goals = goalsData?.data || [];

  // Don't render if no board ID (non-goal-enabled teams like Analytics)
  if (!boardId) return null;

  // Don't render if loading or no goals
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <Target className="w-4 h-4 text-amber-500" />
          Sprint Goals
        </h4>
        <div className="animate-pulse space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (goals.length === 0) {
    return (
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <Target className="w-4 h-4 text-amber-500" />
          Sprint Goals
        </h4>
        <p className="text-sm text-gray-500">No goals defined for this sprint.</p>
        <button
          onClick={() => onNavigate('sprint-goals')}
          className="mt-2 text-sm text-amber-600 hover:text-amber-800 hover:underline"
        >
          Add goals →
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <Target className="w-4 h-4 text-amber-500" />
          Sprint Goals
        </h4>
        <button
          onClick={() => onNavigate('sprint-goals')}
          className="text-xs text-amber-600 hover:text-amber-800 hover:underline"
        >
          Manage Goals
        </button>
      </div>
      <div className="space-y-3">
        {goals.map((goal) => {
          const progress = calculateGoalProgress(goal, sprintIssues);
          const isComplete = progress.percentage === 100 && progress.total > 0;

          return (
            <div key={goal.goal_id} className="group">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-700 truncate flex-1 mr-2" title={goal.title}>
                  {goal.title}
                </span>
                <span className={`text-xs font-medium ${isComplete ? 'text-green-600' : 'text-gray-500'}`}>
                  {progress.total > 0 ? `${progress.done}/${progress.total}` : 'No stories'}
                  {isComplete && ' ✓'}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    isComplete ? 'bg-green-500' : progress.percentage > 0 ? 'bg-amber-500' : 'bg-gray-300'
                  }`}
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>
              {progress.inProgress > 0 && (
                <p className="text-xs text-amber-500 mt-0.5">
                  {progress.inProgress} in progress
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SprintTeamPage({ teamKey, onNavigate }) {
  const [filters, setFilters] = useState({});
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [hasSetDefaultSprint, setHasSetDefaultSprint] = useState(false);
  const issuesTableRef = useRef(null);
  const { data, isLoading, isError, error, refetch, isFetching } = useSprintTeamMetrics(teamKey, filters);

  // Sort state for each table
  // Status table defaults to no sort to preserve backend's custom workflow order (To Do -> Development -> QA Testing -> etc.)
  const [statusSort, handleStatusSort] = useTableSort(null);
  const [assigneeSort, handleAssigneeSort] = useTableSort('todo', 'desc');
  const [issuesSort, handleIssuesSort] = useTableSort(null);
  const [historySort, handleHistorySort] = useTableSort(null);

  // Track expanded parent issues for collapsible sub-tasks
  const [expandedParents, setExpandedParents] = useState(new Set());

  const toggleParentExpanded = (parentKey) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(parentKey)) {
        next.delete(parentKey);
      } else {
        next.add(parentKey);
      }
      return next;
    });
  };

  const expandAllParents = (parentKeys) => {
    setExpandedParents(new Set(parentKeys));
  };

  const collapseAllParents = () => {
    setExpandedParents(new Set());
  };

  const teamName = TEAM_NAMES[teamKey] || teamKey;

  // Set default filter to active sprint when data loads
  const metrics = data?.data;
  const availableSprints = metrics?.availableSprints || [];

  // Auto-select active sprint on first load
  React.useEffect(() => {
    if (!hasSetDefaultSprint && availableSprints.length > 0) {
      const activeSprint = availableSprints.find(s => s.state === 'active');
      if (activeSprint) {
        setFilters({ sprintIds: [activeSprint.id] });
      }
      setHasSetDefaultSprint(true);
    }
  }, [availableSprints, hasSetDefaultSprint]);

  // Reset default sprint selection when team changes
  React.useEffect(() => {
    setHasSetDefaultSprint(false);
    setFilters({});
  }, [teamKey]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleAssigneeClick = (assigneeName) => {
    setSelectedAssignee(assigneeName);
    // Scroll to issues table after a short delay to allow state update
    setTimeout(() => {
      issuesTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const clearAssigneeFilter = () => {
    setSelectedAssignee(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-amber-600 animate-spin" />
          <p className="text-gray-500 text-lg">Loading {teamName} sprint data...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4 text-red-500">
          <AlertCircle className="w-12 h-12" />
          <p className="text-lg font-medium">Failed to load sprint data</p>
          <p className="text-sm text-gray-500">{error?.message}</p>
          <button
            onClick={() => refetch()}
            className="mt-2 px-4 py-2 bg-amber-400 text-gray-900 rounded-lg hover:bg-amber-500"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { velocityTrend, activeSprint } = metrics || {};

  // Prepare velocity chart data
  const velocityChartData = velocityTrend?.map((sprint) => ({
    name: sprint.sprintName?.replace(/Sprint\s*/i, 'S') || 'Unknown',
    completed: sprint.completedPoints,
    spillover: sprint.spilloverPoints,
    total: sprint.totalPoints,
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button
            onClick={() => onNavigate('technology')}
            className="text-sm text-amber-600 hover:text-amber-800 flex items-center gap-1 mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Technology Dashboard
          </button>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Target className="w-7 h-7 text-amber-600" />
            {metrics?.team || teamName}
          </h2>
          <p className="text-gray-500 mt-1">
            Sprint-based team metrics and velocity tracking
          </p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href={`https://sunnyrewards.atlassian.net/jira/software/c/projects/${metrics?.projectKey}/boards/${metrics?.boardId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ExternalLink className="w-4 h-4" />
            Open in Jira
          </a>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-2 px-4 py-2 bg-amber-400 text-gray-900 rounded-lg hover:bg-amber-500 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <TechnologyFilters
        onFilterChange={handleFilterChange}
        showSprintFilter={true}
        availableSprints={metrics?.availableSprints || []}
        initialFilters={filters}
      />

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Sprint Count"
          value={metrics?.sprintCount || 0}
          subtitle="Completed sprints analyzed"
          icon={Target}
          color="blue"
        />
        <StatCard
          title="Avg Velocity"
          value={`${metrics?.avgVelocity || 0} pts`}
          subtitle="Story points per sprint"
          icon={Zap}
          color="green"
        />
        <StatCard
          title="Avg Spillover"
          value={`${metrics?.avgSpillover || 0} pts`}
          subtitle="Points carried over"
          icon={TrendingDown}
          color={metrics?.avgSpillover > 5 ? 'red' : 'orange'}
        />
        <StatCard
          title="Spillover Rate"
          value={`${metrics?.avgVelocity > 0 ? Math.round((metrics?.avgSpillover / (metrics?.avgVelocity + metrics?.avgSpillover)) * 100) : 0}%`}
          subtitle="Of total committed"
          icon={Clock}
          color={metrics?.avgSpillover > 5 ? 'red' : 'green'}
        />
      </div>

      {/* Sprint Detail Section */}
      {activeSprint && (
        <div className={`bg-gradient-to-r ${activeSprint.sprintState === 'closed' ? 'from-gray-50 to-slate-50 border-gray-200' : 'from-amber-50 to-orange-50 border-amber-200'} rounded-lg border p-6`}>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Play className={`w-5 h-5 ${activeSprint.sprintState === 'closed' ? 'text-gray-500' : 'text-amber-600'}`} />
            {activeSprint.sprintState === 'closed' ? 'Completed Sprint' : 'Active Sprint'}: {activeSprint.sprintName}
            {activeSprint.sprintState === 'closed' && (
              <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full ml-2">Closed</span>
            )}
          </h3>

          {/* Row 1: Active Sprint Progress + Issue Type Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Progress Card */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Sprint Progress</h4>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-500">Points Completed</span>
                <span className="text-sm font-bold text-amber-600">
                  {activeSprint.completedPoints} / {activeSprint.totalPoints} pts
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                <div
                  className="bg-amber-500 h-4 rounded-full transition-all flex items-center justify-center text-xs text-white font-medium"
                  style={{ width: `${activeSprint.totalPoints > 0 ? (activeSprint.completedPoints / activeSprint.totalPoints) * 100 : 0}%` }}
                >
                  {activeSprint.totalPoints > 0 ? Math.round((activeSprint.completedPoints / activeSprint.totalPoints) * 100) : 0}%
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-gray-600">{activeSprint.todoCount}</p>
                  <p className="text-xs text-gray-500">To Do</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-amber-600">{activeSprint.inProgressCount}</p>
                  <p className="text-xs text-amber-500">In Progress</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-green-600">{activeSprint.completedCount}</p>
                  <p className="text-xs text-green-500">Done</p>
                </div>
              </div>
            </div>

            {/* Issue Type Breakdown */}
            {activeSprint.issueTypeBreakdown && (
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-purple-500" />
                  Issue Type Breakdown
                </h4>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  <IssueTypeCard
                    title="Stories"
                    icon={BookOpen}
                    color="blue"
                    data={activeSprint.issueTypeBreakdown.stories}
                  />
                  <IssueTypeCard
                    title="Bugs"
                    icon={Bug}
                    color="red"
                    data={activeSprint.issueTypeBreakdown.bugs}
                  />
                  <IssueTypeCard
                    title="Tasks"
                    icon={ClipboardList}
                    color="indigo"
                    data={activeSprint.issueTypeBreakdown.tasks}
                  />
                  <IssueTypeCard
                    title="Sub-tasks"
                    icon={ListTodo}
                    color="purple"
                    data={activeSprint.issueTypeBreakdown.subTasks}
                  />
                  <IssueTypeCard
                    title="Defects"
                    icon={AlertTriangle}
                    color="orange"
                    data={activeSprint.issueTypeBreakdown.defects}
                  />
                  <IssueTypeCard
                    title="Other"
                    icon={Layers}
                    color="gray"
                    data={activeSprint.issueTypeBreakdown.others}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Sprint Goals Progress */}
          {BOARD_IDS[teamKey] && (
            <div className="mt-4">
              <SprintGoalsSection
                boardId={BOARD_IDS[teamKey]}
                sprintId={activeSprint.sprintId}
                sprintIssues={activeSprint.issues || []}
                onNavigate={onNavigate}
              />
            </div>
          )}

          {/* Row 2: Story Points by Status + Status Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            {/* Story Points by Status - Vertical Bar Chart */}
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Story Points by Status</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={activeSprint.detailedStatusBreakdown?.map(s => ({
                      name: s.status,
                      points: s.points,
                      count: s.count,
                      fill: s.category === 'done' ? '#22C55E' : s.category === 'indeterminate' ? '#3B82F6' : '#9CA3AF',
                    })) || []}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, angle: -45, textAnchor: 'end' }} height={60} />
                    <YAxis />
                    <Tooltip
                      formatter={(value, name) => [value, name === 'points' ? 'Points' : 'Issues']}
                      labelFormatter={(label) => `Status: ${label}`}
                    />
                    <Bar dataKey="points" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Status Breakdown */}
            {activeSprint.statusBreakdown && (
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Status Breakdown
                </h4>
                <div className="space-y-2">
                  <StatusCard
                    title="To Do"
                    icon={Circle}
                    color="gray"
                    data={activeSprint.statusBreakdown.todo}
                  />
                  <StatusCard
                    title="In Progress"
                    icon={Play}
                    color="blue"
                    data={activeSprint.statusBreakdown.inProgress}
                  />
                  <StatusCard
                    title="Done"
                    icon={CheckCircle}
                    color="green"
                    data={activeSprint.statusBreakdown.completed}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Issues by Workflow Status - Data Table */}
          {activeSprint.detailedStatusBreakdown && activeSprint.detailedStatusBreakdown.length > 0 && (
            <div className="mt-4 bg-white rounded-lg p-4 shadow-sm">
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Layers className="w-4 h-4 text-amber-500" />
                Issues by Workflow Status
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <SortableHeader label="Status" sortKey="status" currentSort={statusSort} onSort={handleStatusSort} />
                      <SortableHeader label="Category" sortKey="category" currentSort={statusSort} onSort={handleStatusSort} />
                      <SortableHeader label="Issues" sortKey="count" currentSort={statusSort} onSort={handleStatusSort} align="right" />
                      <SortableHeader label="%" sortKey="percentage" currentSort={statusSort} onSort={handleStatusSort} align="right" />
                      <SortableHeader label="Points" sortKey="points" currentSort={statusSort} onSort={handleStatusSort} align="right" />
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(() => {
                      const dataWithPercentage = activeSprint.detailedStatusBreakdown.map(status => ({
                        ...status,
                        percentage: activeSprint.issueCount > 0 ? Math.round((status.count / activeSprint.issueCount) * 100) : 0,
                      }));
                      const sortedData = sortData(dataWithPercentage, statusSort.key, statusSort.direction);
                      return sortedData.map((status) => {
                        const categoryColor = status.category === 'done' ? 'bg-green-100 text-green-800' :
                                              status.category === 'indeterminate' ? 'bg-amber-100 text-amber-800' :
                                              'bg-gray-100 text-gray-800';
                        const categoryLabel = status.category === 'done' ? 'Done' :
                                              status.category === 'indeterminate' ? 'In Progress' : 'To Do';
                        return (
                          <tr key={status.status} className="hover:bg-gray-50">
                            <td className="px-4 py-2 text-sm font-medium text-gray-900">{status.status}</td>
                            <td className="px-4 py-2">
                              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${categoryColor}`}>
                                {categoryLabel}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-sm text-right font-medium">{status.count}</td>
                            <td className="px-4 py-2 text-sm text-right text-gray-500">{status.percentage}%</td>
                            <td className="px-4 py-2 text-sm text-right font-medium text-amber-600">{status.points} pts</td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Assignee Breakdown */}
          {activeSprint.assigneeBreakdown && activeSprint.assigneeBreakdown.length > 0 && (
            <div className="mt-4 bg-white rounded-lg p-4 shadow-sm">
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-indigo-500" />
                Work Items by Assignee
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <SortableHeader label="Assignee" sortKey="assignee" currentSort={assigneeSort} onSort={handleAssigneeSort} />
                      <SortableHeader label="Issues" sortKey="count" currentSort={assigneeSort} onSort={handleAssigneeSort} align="right" />
                      <SortableHeader label="%" sortKey="percentage" currentSort={assigneeSort} onSort={handleAssigneeSort} align="right" />
                      <SortableHeader label="Points" sortKey="points" currentSort={assigneeSort} onSort={handleAssigneeSort} align="right" />
                      <SortableHeader label="To Do" sortKey="todo" currentSort={assigneeSort} onSort={handleAssigneeSort} align="center" />
                      <SortableHeader label="In Progress" sortKey="inProgress" currentSort={assigneeSort} onSort={handleAssigneeSort} align="center" />
                      <SortableHeader label="Done" sortKey="completed" currentSort={assigneeSort} onSort={handleAssigneeSort} align="center" />
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(() => {
                      const dataWithPercentage = activeSprint.assigneeBreakdown.map(assignee => ({
                        ...assignee,
                        percentage: activeSprint.issueCount > 0 ? Math.round((assignee.count / activeSprint.issueCount) * 100) : 0,
                      }));
                      const sortedData = sortData(dataWithPercentage, assigneeSort.key, assigneeSort.direction);
                      return sortedData.map((assignee) => {
                        const isSelected = selectedAssignee === assignee.assignee;
                        return (
                          <tr key={assignee.assignee} className={`hover:bg-gray-50 ${isSelected ? 'bg-indigo-50' : ''}`}>
                            <td className="px-4 py-2 text-sm font-medium">
                              <button
                                onClick={() => handleAssigneeClick(assignee.assignee)}
                                className={`text-left hover:underline ${isSelected ? 'text-indigo-600 font-semibold' : 'text-amber-600 hover:text-amber-800'}`}
                              >
                                {assignee.assignee}
                              </button>
                            </td>
                            <td className="px-4 py-2 text-sm text-right font-medium">{assignee.count}</td>
                            <td className="px-4 py-2 text-sm text-right text-gray-500">{assignee.percentage}%</td>
                            <td className="px-4 py-2 text-sm text-right font-medium text-amber-600">{assignee.points} pts</td>
                            <td className="px-4 py-2 text-center">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                                {assignee.todo}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-center">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-600 text-xs font-medium">
                                {assignee.inProgress}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-center">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600 text-xs font-medium">
                                {assignee.completed}
                              </span>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Active Sprint Issues */}
          {activeSprint.issues && activeSprint.issues.length > 0 && (
            <div ref={issuesTableRef} className="mt-4 bg-white rounded-lg p-4 shadow-sm">
              {(() => {
                // Filter issues by assignee if selected
                const filteredIssues = selectedAssignee
                  ? activeSprint.issues.filter(issue => (issue.assignee || 'Unassigned') === selectedAssignee)
                  : activeSprint.issues;

                // Group issues: parent issues and their sub-tasks
                const parentIssues = filteredIssues.filter(issue => !issue.isSubTask);
                const subTasksByParent = {};
                const orphanSubTasks = [];

                filteredIssues.filter(issue => issue.isSubTask).forEach(subTask => {
                  if (subTask.parentKey) {
                    if (!subTasksByParent[subTask.parentKey]) {
                      subTasksByParent[subTask.parentKey] = [];
                    }
                    subTasksByParent[subTask.parentKey].push(subTask);
                  } else {
                    orphanSubTasks.push(subTask);
                  }
                });

                // Get all parent keys that have sub-tasks
                const parentsWithSubTasks = Object.keys(subTasksByParent);
                const subTaskCount = Object.values(subTasksByParent).reduce((sum, arr) => sum + arr.length, 0) + orphanSubTasks.length;

                // Sort parent issues
                const sortedParents = sortData(parentIssues, issuesSort.key, issuesSort.direction);

                return (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        Sprint Issues
                        {selectedAssignee ? (
                          <>
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium">
                              <Users className="w-3 h-3" />
                              {selectedAssignee}
                              <button
                                onClick={clearAssigneeFilter}
                                className="ml-1 hover:bg-indigo-200 rounded-full p-0.5"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                            <span className="text-gray-400">({filteredIssues.length} of {activeSprint.issues.length})</span>
                          </>
                        ) : (
                          <span className="text-gray-400">({activeSprint.issues.length})</span>
                        )}
                        {subTaskCount > 0 && (
                          <span className="text-xs text-gray-400">
                            • {parentIssues.length} parent{parentIssues.length !== 1 ? 's' : ''}, {subTaskCount} sub-task{subTaskCount !== 1 ? 's' : ''}
                          </span>
                        )}
                      </h4>
                      <div className="flex items-center gap-2">
                        {parentsWithSubTasks.length > 0 && (
                          <>
                            <button
                              onClick={() => expandAllParents(parentsWithSubTasks)}
                              className="text-xs text-amber-600 hover:text-amber-800"
                            >
                              Expand all
                            </button>
                            <span className="text-gray-300">|</span>
                            <button
                              onClick={collapseAllParents}
                              className="text-xs text-amber-600 hover:text-amber-800"
                            >
                              Collapse all
                            </button>
                          </>
                        )}
                        {selectedAssignee && (
                          <>
                            <span className="text-gray-300">|</span>
                            <button
                              onClick={clearAssigneeFilter}
                              className="text-xs text-gray-500 hover:text-gray-700"
                            >
                              Show all issues
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase w-8"></th>
                            <SortableHeader label="Key" sortKey="key" currentSort={issuesSort} onSort={handleIssuesSort} />
                            <SortableHeader label="Summary" sortKey="summary" currentSort={issuesSort} onSort={handleIssuesSort} />
                            <SortableHeader label="Status" sortKey="status" currentSort={issuesSort} onSort={handleIssuesSort} />
                            <SortableHeader label="Type" sortKey="type" currentSort={issuesSort} onSort={handleIssuesSort} />
                            <SortableHeader label="Points" sortKey="storyPoints" currentSort={issuesSort} onSort={handleIssuesSort} align="right" />
                            <SortableHeader label="Assignee" sortKey="assignee" currentSort={issuesSort} onSort={handleIssuesSort} />
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {sortedParents.map((issue) => {
                            const childSubTasks = subTasksByParent[issue.key] || [];
                            const hasChildren = childSubTasks.length > 0;
                            const isExpanded = expandedParents.has(issue.key);

                            return (
                              <React.Fragment key={issue.key}>
                                {/* Parent Issue Row */}
                                <tr className="hover:bg-gray-50">
                                  <td className="px-2 py-2 text-sm w-8">
                                    {hasChildren ? (
                                      <button
                                        onClick={() => toggleParentExpanded(issue.key)}
                                        className="p-1 hover:bg-gray-200 rounded"
                                        title={isExpanded ? 'Collapse sub-tasks' : `Expand ${childSubTasks.length} sub-task${childSubTasks.length > 1 ? 's' : ''}`}
                                      >
                                        {isExpanded ? (
                                          <ChevronDown className="w-4 h-4 text-gray-500" />
                                        ) : (
                                          <ChevronRight className="w-4 h-4 text-gray-500" />
                                        )}
                                      </button>
                                    ) : (
                                      <span className="w-6 inline-block" />
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-sm">
                                    <div className="flex items-center gap-1">
                                      <a
                                        href={`https://sunnyrewards.atlassian.net/browse/${issue.key}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-amber-600 hover:underline font-medium"
                                      >
                                        {issue.key}
                                      </a>
                                      {hasChildren && !isExpanded && (
                                        <span className="text-xs text-gray-400 ml-1">
                                          (+{childSubTasks.length})
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900 max-w-xs truncate">{issue.summary}</td>
                                  <td className="px-4 py-2">
                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(issue.statusCategory)}`}>
                                      {getStatusIcon(issue.statusCategory)}
                                      {issue.status}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-600">{issue.type}</td>
                                  <td className="px-4 py-2 text-sm text-right font-medium">{issue.storyPoints || '-'}</td>
                                  <td className="px-4 py-2 text-sm text-gray-600">{issue.assignee || 'Unassigned'}</td>
                                </tr>
                                {/* Sub-task Rows (collapsed by default) */}
                                {isExpanded && childSubTasks.map((subTask) => (
                                  <tr key={subTask.key} className="hover:bg-purple-50 bg-gray-50">
                                    <td className="px-2 py-2 text-sm w-8"></td>
                                    <td className="px-4 py-2 text-sm">
                                      <div className="flex items-center gap-1 pl-4">
                                        <span className="text-gray-300 mr-1">└</span>
                                        <a
                                          href={`https://sunnyrewards.atlassian.net/browse/${subTask.key}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-purple-600 hover:underline font-medium"
                                        >
                                          {subTask.key}
                                        </a>
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700 max-w-xs truncate">{subTask.summary}</td>
                                    <td className="px-4 py-2">
                                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(subTask.statusCategory)}`}>
                                        {getStatusIcon(subTask.statusCategory)}
                                        {subTask.status}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-500">{subTask.type}</td>
                                    <td className="px-4 py-2 text-sm text-right font-medium text-gray-500">{subTask.storyPoints || '-'}</td>
                                    <td className="px-4 py-2 text-sm text-gray-500">{subTask.assignee || 'Unassigned'}</td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}
                          {/* Orphan sub-tasks (no parent in current filter) */}
                          {orphanSubTasks.length > 0 && (
                            <>
                              <tr className="bg-yellow-50">
                                <td colSpan={7} className="px-4 py-2 text-xs text-yellow-700 font-medium">
                                  Sub-tasks without parent in current view ({orphanSubTasks.length})
                                </td>
                              </tr>
                              {orphanSubTasks.map((subTask) => (
                                <tr key={subTask.key} className="hover:bg-yellow-50 bg-yellow-25">
                                  <td className="px-2 py-2 text-sm w-8"></td>
                                  <td className="px-4 py-2 text-sm">
                                    <a
                                      href={`https://sunnyrewards.atlassian.net/browse/${subTask.key}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-purple-600 hover:underline font-medium"
                                    >
                                      {subTask.key}
                                    </a>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-700 max-w-xs truncate">{subTask.summary}</td>
                                  <td className="px-4 py-2">
                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(subTask.statusCategory)}`}>
                                      {getStatusIcon(subTask.statusCategory)}
                                      {subTask.status}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-500">{subTask.type}</td>
                                  <td className="px-4 py-2 text-sm text-right font-medium text-gray-500">{subTask.storyPoints || '-'}</td>
                                  <td className="px-4 py-2 text-sm text-gray-500">{subTask.assignee || 'Unassigned'}</td>
                                </tr>
                              ))}
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Velocity Trend Chart */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-500" />
          Velocity Trend (Last {velocityTrend?.length || 0} Sprints)
        </h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={velocityChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area
                type="monotone"
                dataKey="completed"
                name="Completed"
                stackId="1"
                stroke="#22C55E"
                fill="#22C55E"
                fillOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey="spillover"
                name="Spillover"
                stackId="1"
                stroke="#EF4444"
                fill="#EF4444"
                fillOpacity={0.6}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sprint History Table */}
      {velocityTrend && velocityTrend.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-500" />
            Sprint History
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader label="Sprint" sortKey="sprintName" currentSort={historySort} onSort={handleHistorySort} />
                  <SortableHeader label="Dates" sortKey="startDate" currentSort={historySort} onSort={handleHistorySort} />
                  <SortableHeader label="Committed" sortKey="totalPoints" currentSort={historySort} onSort={handleHistorySort} align="right" />
                  <SortableHeader label="Completed" sortKey="completedPoints" currentSort={historySort} onSort={handleHistorySort} align="right" />
                  <SortableHeader label="Spillover" sortKey="spilloverPoints" currentSort={historySort} onSort={handleHistorySort} align="right" />
                  <SortableHeader label="Issues" sortKey="issueCount" currentSort={historySort} onSort={handleHistorySort} align="right" />
                  <SortableHeader label="Completion" sortKey="completionRate" currentSort={historySort} onSort={handleHistorySort} align="right" />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(() => {
                  const dataWithCompletion = velocityTrend.slice().reverse().map(sprint => ({
                    ...sprint,
                    completionRate: sprint.totalPoints > 0 ? Math.round((sprint.completedPoints / sprint.totalPoints) * 100) : 0,
                  }));
                  const sortedData = historySort.key ? sortData(dataWithCompletion, historySort.key, historySort.direction) : dataWithCompletion;
                  return sortedData.map((sprint) => (
                    <tr key={sprint.sprintId} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{sprint.sprintName}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {sprint.startDate?.split('T')[0]} → {sprint.endDate?.split('T')[0]}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium">{sprint.totalPoints} pts</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-green-600">{sprint.completedPoints} pts</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-red-600">{sprint.spilloverPoints} pts</td>
                      <td className="px-4 py-3 text-sm text-right">
                        {sprint.completedCount} / {sprint.issueCount}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          sprint.completionRate >= 80
                            ? 'bg-green-100 text-green-800'
                            : sprint.completionRate >= 60
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {sprint.completionRate}%
                        </span>
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
